import { getPinakesUrl, PINAKES_REQUEST_TIMEOUT_MS, PINAKES_URI_MAX_BYTES } from '@/config/pinakes';
import { httpResponseToError, safeFetch } from '@/libs/error/error.http';
import { ErrorService } from '@/libs/error/error.types';
import { HttpMethod, HttpStatusCode } from '@/libs/http/http.types';
import { parseResponseOrThrow } from '@/libs/http/response.utils';
import { pinakesCardStaleTime, pinakesQueryClient } from './pinakes.query-client';
import type {
  TPinakesCard,
  TPinakesGroupDetail,
  TPinakesResourceDetail,
  TPinakesSearchResponse,
} from './pinakes.types';

const BY_URI_PATH = '/v1/resources/by-uri';
const SEARCH_PATH = '/v1/search';
const GROUPS_PATH = '/v1/groups';

/**
 * Pinakes service: read-only reads of the public Pinakes API (`PUBKY_RUNTIME_PINAKES_URL`),
 * which reads the external links people reference on Pubky into typed cards.
 *
 * Every read goes through `pinakesQueryClient` (cache, in-flight dedupe, retry). With no Pinakes
 * origin configured every read answers "nothing" without a request, so the feature is off.
 * Nothing is persisted to IndexedDB: a card is enrichment of a link, like its Open Graph preview.
 */
export class PinakesService {
  private constructor() {}

  /**
   * One link's card by its URI (`GET /v1/resources/by-uri`).
   *
   * @param uri - The link as referenced; Pinakes normalises it and answers an alias with its root
   * @returns The card, or `null` when Pinakes does not know the link (404, nothing is fetched there),
   * the link is too long to be known, or Pinakes is not configured
   * @throws AppError when Pinakes fails or answers with anything else
   */
  static async fetchCardByUri(uri: string): Promise<TPinakesResourceDetail | null> {
    const base = getPinakesBaseUrl();
    if (!base || new TextEncoder().encode(uri).length > PINAKES_URI_MAX_BYTES) return null;

    return pinakesQueryClient.fetchQuery({
      queryKey: ['pinakes', 'by-uri', uri],
      staleTime: pinakesCardStaleTime,
      queryFn: () =>
        fetchPinakesOrNull<TPinakesResourceDetail>({
          url: `${base}${BY_URI_PATH}?${new URLSearchParams({ uri })}`,
          endpoint: `${base}${BY_URI_PATH}`,
          operation: 'fetchCardByUri',
        }),
    });
  }

  /**
   * What the people who tagged a resource also referenced (`GET /v1/search?related=<id>`), ranked
   * by how many of them did. A resource in a group stands for the group, whose members are never
   * listed.
   *
   * @param id - Pinakes resource ID
   * @param limit - Results to ask for (1-500)
   * @returns The related cards, `[]` when there are none or Pinakes is not configured
   * @throws AppError when Pinakes fails
   */
  static async fetchRelated(id: string, limit: number): Promise<TPinakesCard[]> {
    const base = getPinakesBaseUrl();
    if (!base) return [];

    const response = await pinakesQueryClient.fetchQuery({
      queryKey: ['pinakes', 'related', id, limit],
      queryFn: () =>
        fetchPinakesOrNull<TPinakesSearchResponse>({
          url: `${base}${SEARCH_PATH}?${new URLSearchParams({ related: id, limit: String(limit) })}`,
          endpoint: `${base}${SEARCH_PATH}`,
          operation: 'fetchRelated',
        }),
    });
    return response?.items ?? [];
  }

  /**
   * A group's members, every member's own card (`GET /v1/groups/{id}`).
   *
   * @param id - Group ID, from a card's `group.id`
   * @returns The group, or `null` when it no longer exists (404) or Pinakes is not configured
   * @throws AppError when Pinakes fails
   */
  static async fetchGroup(id: string): Promise<TPinakesGroupDetail | null> {
    const base = getPinakesBaseUrl();
    if (!base) return null;

    const endpoint = `${base}${GROUPS_PATH}/${encodeURIComponent(id)}`;
    return pinakesQueryClient.fetchQuery({
      queryKey: ['pinakes', 'group', id],
      queryFn: () => fetchPinakesOrNull<TPinakesGroupDetail>({ url: endpoint, endpoint, operation: 'fetchGroup' }),
    });
  }
}

/** The configured origin without trailing slashes, so a base with a path prefix keeps it. */
function getPinakesBaseUrl(): string | null {
  const base = getPinakesUrl();
  return base ? base.replace(/\/+$/, '') : null;
}

/**
 * One GET with no retry of its own, ended after `PINAKES_REQUEST_TIMEOUT_MS`. A 404 is an answer
 * ("Pinakes does not know it"), not a failure, so it returns `null` without creating an AppError.
 * A timeout or connection failure is a Network AppError the `pinakes-transport` Sentry drop rule
 * keeps out of telemetry.
 *
 * `endpoint` is the request without its query string: it goes into error context, and a
 * `by-uri` query carries a link from someone's post.
 */
async function fetchPinakesOrNull<T>({
  url,
  endpoint,
  operation,
}: {
  url: string;
  endpoint: string;
  operation: string;
}): Promise<T | null> {
  const response = await safeFetch(
    url,
    { method: HttpMethod.GET, signal: AbortSignal.timeout(PINAKES_REQUEST_TIMEOUT_MS) },
    ErrorService.Pinakes,
    operation,
  );
  if (response.status === HttpStatusCode.NOT_FOUND) {
    response.body?.cancel().catch(() => {});
    return null;
  }
  if (!response.ok) {
    throw httpResponseToError(response, ErrorService.Pinakes, operation, endpoint);
  }
  return parseResponseOrThrow<T>(response, ErrorService.Pinakes, operation, endpoint);
}
